import type { ColumnDef } from "@tanstack/react-table";

import { useNavigate } from "react-router-dom";
import { Square, SquareCheck } from "lucide-react";

import { DataTableCenteredCell } from "@/shared/ui/DataTable";
import { Button } from "@/shared/ui/button.tsx";
import { formatDate, numberWithSpaces } from "@/shared/lib/utils";
import { SortButton } from "@/features/SortButton/SortButton";
import { getUSDTBalance } from "@/entities/balance/utils/get-balance";
import { UnnasignedTgTooltip } from "@/features/UnnasignedTgTooltip";
import { SendUserDataButton } from "@/features/SendUserDataButton";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { HandleSelectDPUserButton } from "@/pages/restricted/shared/main-page/ui/head/HeadDashboardProDataTable";
import { Roles } from "@/entities/user";
import { useIsCurrentlyAtDashboardPro } from "@/shared/hooks";
import { Worker } from "@/entities/worker";
import { MotiveGridModal } from "@/widgets/MotiveGridModal";

export const WorkersColumns: ColumnDef<Worker>[] = [
  {
    id: "sendUserData",
    header: () => {
      const isMobile = useIsMobile();
      return isMobile ? "Отправить данные" : null;
    },
    cell: ({ row }) => {
      const { _id } = row.original;
      return <SendUserDataButton _id={_id} />;
    },
  },
  {
    accessorKey: "login",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        Логин
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => {
      const isCurrentlyAtDashboardPro = useIsCurrentlyAtDashboardPro([
        Roles.Head,
      ]);

      const navigate = useNavigate();
      const handleNavigate = () => {
        navigate(`/head/access?search=${row.getValue("login")}`);
      };

      return (
        <DataTableCenteredCell>
          <div className="relative">
            {isCurrentlyAtDashboardPro ? (
              <HandleSelectDPUserButton
                userId={row.original._id || ""}
                userName={row.original.login.replace("𒐫", "") || "N/A"}
                userRole={Roles.Worker}
              />
            ) : (
              <>
                <Button
                  variant="link"
                  className="text-blue-500"
                  onClick={handleNavigate}
                >
                  {row.original.login?.replace("𒐫", "") || "N/A"}
                </Button>
                {row.original.tgId === null && (
                  <UnnasignedTgTooltip offset={-0.5} />
                )}
              </>
            )}
          </div>
        </DataTableCenteredCell>
      );
    },
  },
  {
    accessorKey: "balance",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort="usdtBalance.balance" title="Баланс" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {numberWithSpaces(getUSDTBalance(row.original.balances)[0])}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "totalTurnover",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort="totalTurnover" title="Оборот" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.totalTurnover}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "totalLeadsCount",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort="totalLeadsCount" title="Лидов" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.totalLeadsCount}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "closedLeadsCount",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort="closedLeadsCount" title="Закрытий" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.closedLeadsCount}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "activeCommissionExclusionsCount",
    header: () => (
      <DataTableCenteredCell>
        <SortButton
          sort="activeCommissionExclusionsCount"
          title="Мотив. сетка"
        />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => {
      return (
        <DataTableCenteredCell>
          {row.original.activeCommissionExclusionsCount !== 0 ? (
            <SquareCheck />
          ) : (
            <Square />
          )}
        </DataTableCenteredCell>
      );
    },
  },
  {
    accessorKey: "lastLeadEnterAt",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        Последний лид
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.lastLeadEnterAt ? (
          <p className="text-textColor">
            {formatDate(row.original.lastLeadEnterAt)}
          </p>
        ) : (
          "N/A"
        )}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "actions",
    header: () => {
      return <DataTableCenteredCell>Действия</DataTableCenteredCell>;
    },
    cell: ({ row }) => {
      return (
        <DataTableCenteredCell>
          <MotiveGridModal
            nickname={row.original.login}
            role={Roles.Worker}
            userId={row.original.user._id}
          />
        </DataTableCenteredCell>
      );
    },
  },
];
